import type {
  InvoiceEmailListQueryParams,
  InvoiceEmailListResponse,
} from './types';

import {
  keepPreviousData,
  queryOptions,
  type DefaultError,
  type QueryFunctionContext,
  type UseQueryOptions,
  useQuery,
} from '@tanstack/react-query';
import { ms } from 'ms';

import { fetchInvoiceEmails } from './fetchInvoiceEmails';
import { queryKeysInvoiceEmail } from './queryKeys';

/**
 * Generates query options for the paginated invoice email inbox endpoint.
 *
 * @param queryParams - Search, filter, sort, and pagination parameters.
 * @param options - Additional query options.
 * @returns Query options for the invoice email list.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice-Emails/operation/list_invoice_emails_invoice_emails_get
 */
// fallow-ignore-next-line unused-export -- Query options are part of the data-layer API.
export function getInvoiceEmailsQueryOptions<TData = InvoiceEmailListResponse>({
  queryParams,
  options,
}: {
  queryParams: Partial<InvoiceEmailListQueryParams>;
  options?: Omit<
    UseQueryOptions<InvoiceEmailListResponse, DefaultError, TData>,
    'queryKey' | 'queryFn'
  >;
}) {
  return queryOptions<InvoiceEmailListResponse, DefaultError, TData>({
    queryFn: (context: QueryFunctionContext) =>
      fetchInvoiceEmails(queryParams, context),
    queryKey: queryKeysInvoiceEmail.getList(queryParams),
    staleTime: ms('30s'),
    ...options,
  });
}

/**
 * Loads the invoice email inbox.
 *
 * @param queryParams - Search, filter, sort, and pagination parameters.
 * @param options - Additional query options.
 * @returns TanStack Query result containing the invoice email page.
 */
export function useQueryInvoiceEmails<TData = InvoiceEmailListResponse>(
  queryParams: Partial<InvoiceEmailListQueryParams>,
  options?: Omit<
    UseQueryOptions<InvoiceEmailListResponse, DefaultError, TData>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    ...getInvoiceEmailsQueryOptions<TData>({ options, queryParams }),
    placeholderData: keepPreviousData,
  });
}
